import { generateApiRoute } from '@klp/core/api/helpers';
import { baseApi } from '@klp/core/api';

export const prosjektProcessApiName = 'prosjekt/process';

const eosApiName = 'eos';
const prosjektAvfallEndpoint = 'ProsjektAvfall';
const typesEndpoint = 'avfallTypes';
const analyzeEndpoint = 'analyze';
const updateEndpoint = 'update';
const klimagassberegningEndpoint = 'klimagassberegning';

export type AvfallType = {
    id: number;
    avfallTypeId: number;
    beskrivelse: string;
};

export type Avfall = {
    id: number;
    sortert: boolean | null;
    avfallType: string;
    avfallTypeId: number | null;
    antallTransporter: number;
    beskrivelse: string;
    mengde: number;
};

export type ProsjektAvfall = {
    id: number | null;
    prosessInstansId: string;
    prosjekttype: string;
    prosjektnummer: string;
    prosjektId: string;
    prosjektnavn: string;
    prosjektleder: string;
    region: string;
    byggnavn: string;
    byggtype: string;
    rapportDato?: Date;
    rapportPeriodeFra?: Date;
    rapportPeriodeTil?: Date;
    avfallDokumentId: number | null;
    avfall: Array<Avfall>;
    totalTransporter: number;
    totalMengdeSortert: number;
    totalMengdeUsortert: number;
    totalMengde: number;
    klimagassberegning: number | null;
    /** Project leader's answer at project start: is a greenhouse gas calculation relevant? Null when not answered. */
    klimagassberegningRelevant: boolean | null;
    /** The project leader's explanation when the calculation is not relevant. */
    klimagassberegningForklaring: string | null;
};

type FileRequest = {
    name: string;
    type?: string;
    content?: string;
};

type PatchProsjektAvfallRequest = {
    id: number;
    avfall: Array<Avfall>;
    klimagassberegning: number | null;
};

type PatchKlimagassberegningRequest = {
    prosessInstansId: string;
    klimagassberegning: number | null;
};

export const eosApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getProsjektAvfallTypes: build.query<Array<AvfallType>, void>({
            query: () => ({
                url: generateApiRoute([
                    eosApiName,
                    prosjektAvfallEndpoint,
                    typesEndpoint,
                ]),
                method: 'GET',
            }),
        }),
        getProsjektAvfall: build.query<
            Array<ProsjektAvfall>,
            { avsluttetFromDate?: string; avsluttetToDate?: string }
        >({
            query: (params) => ({
                url: generateApiRoute([eosApiName, prosjektAvfallEndpoint]),
                params: params,
                method: 'GET',
            }),
            providesTags: ['ProsjektAvfall'],
        }),
        postProsjektAvfall: build.mutation<
            void,
            { prosessInstansId: string; file: FileRequest }
        >({
            query: ({ prosessInstansId, file }) => ({
                url: generateApiRoute([
                    eosApiName,
                    prosjektAvfallEndpoint,
                    prosessInstansId,
                ]),
                method: 'POST',
                body: file,
            }),
        }),
        analyzeProsjektAvfall: build.mutation<
            void,
            { prosessInstansId: string; saveResult?: boolean }
        >({
            query: ({ prosessInstansId, saveResult }) => ({
                url: generateApiRoute([
                    eosApiName,
                    prosjektAvfallEndpoint,
                    analyzeEndpoint,
                    prosessInstansId,
                ]),
                params: { saveResult },
                method: 'POST',
            }),
            invalidatesTags: (_r, _e, { saveResult }) =>
                saveResult ? ['ProsjektAvfall'] : [],
        }),
        updateProsjektAvfall: build.mutation<void, PatchProsjektAvfallRequest>({
            query: (request) => ({
                url: generateApiRoute([
                    eosApiName,
                    prosjektAvfallEndpoint,
                    updateEndpoint,
                ]),
                method: 'PATCH',
                body: request,
            }),
            invalidatesTags: ['ProsjektAvfall'],
        }),
        setProsjektAvfallKlimagassberegning: build.mutation<
            ProsjektAvfall,
            PatchKlimagassberegningRequest
        >({
            query: ({ prosessInstansId, klimagassberegning }) => ({
                url: generateApiRoute([
                    eosApiName,
                    prosjektAvfallEndpoint,
                    prosessInstansId,
                    klimagassberegningEndpoint,
                ]),
                method: 'PATCH',
                body: { klimagassberegning },
            }),
            invalidatesTags: ['ProsjektAvfall'],
        }),
    }),
});

export const {
    useGetProsjektAvfallTypesQuery,
    useGetProsjektAvfallQuery,
    usePostProsjektAvfallMutation,
    useAnalyzeProsjektAvfallMutation,
    useUpdateProsjektAvfallMutation,
    useSetProsjektAvfallKlimagassberegningMutation,
} = eosApi;
